import {
  applyNodeChanges,
  applyEdgeChanges,
} from "@xyflow/react";
import type {
  Edge,
  Node,
  EdgeChange,
  NodeChange,
  OnNodesChange,
  OnEdgesChange,
  XYPosition,
} from "@xyflow/react";
import { createWithEqualityFn } from "zustand/traditional";
import { shallow } from "zustand/shallow";
import { nanoid } from "nanoid/non-secure";
import { produce, enableMapSet } from "immer";
import { getCalculatedValue } from "../Helpers/Calculations";
import { sanitizeLabel } from "../utils/textFormatting";
import {
  viewTypes,
  NodeData,
  OneNotePage,
  CustomProperty,
  values,
  Address,
  Task,
  User,
} from "../types/types";

// Enable Immer MapSet plugin for Map/Set support
enableMapSet();

// Debounce utility for performance optimization
let debounceTimer: NodeJS.Timeout | null = null;
const debounce = (func: () => void, delay: number) => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(func, delay);
};

// React Flow nested nodes require parent nodes to appear before their children
// in the nodes array. Keep ordering stable while enforcing that constraint.
const orderNodesParentBeforeChild = (
  nodes: Node<NodeData>[],
): Node<NodeData>[] => {
  if (nodes.length < 2) return nodes;

  const entries = nodes.map((node, index) => ({ node, index }));
  const byId = new Map(entries.map((e) => [e.node.id, e] as const));

  const childrenByParent = new Map<string, Array<(typeof entries)[number]>>();
  const roots: Array<(typeof entries)[number]> = [];

  for (const entry of entries) {
    const parentId = entry.node.parentId;
    if (!parentId || !byId.has(parentId)) {
      roots.push(entry);
      continue;
    }

    const list = childrenByParent.get(parentId) || [];
    list.push(entry);
    childrenByParent.set(parentId, list);
  }

  roots.sort((a, b) => a.index - b.index);
  for (const list of childrenByParent.values()) {
    list.sort((a, b) => a.index - b.index);
  }

  const visited = new Set<string>();
  const ordered: Node<NodeData>[] = [];

  const visit = (entry: (typeof entries)[number]) => {
    const nodeId = entry.node.id;
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    ordered.push(entry.node);
    const children = childrenByParent.get(nodeId);
    if (children) {
      for (const child of children) visit(child);
    }
  };

  for (const root of roots) visit(root);

  // Include any remaining nodes (cycles, disconnected, etc.) in stable order.
  for (const entry of entries.sort((a, b) => a.index - b.index)) {
    if (!visited.has(entry.node.id)) ordered.push(entry.node);
  }

  return ordered;
};

const NODE_ORIGIN = { x: 0.5, y: 0.5 };

const getNodeSize = (
  node: Node<NodeData>,
): { width: number; height: number } => {
  const width =
    (node as any)?.measured?.width ??
    (node as any)?.width ??
    node.data?.objectWidth ??
    0;
  const height =
    (node as any)?.measured?.height ??
    (node as any)?.height ??
    node.data?.objectHeight ??
    0;
  return {
    width: typeof width === "number" ? width : 0,
    height: typeof height === "number" ? height : 0,
  };
};

const getNodeAbsTopLeft = (node: Node<NodeData>): { x: number; y: number } => {
  return (node as any)?.internals?.positionAbsolute || node.position;
};

export type RFState = {
  oneNotePages: OneNotePage[];
  nodes: Node<NodeData>[];
  nodeMap: Map<string, Node<NodeData>>; // O(1) node lookups
  childrenMap: Map<string, string[]>; // O(1) child node lookups: parentId -> [childId1, childId2, ...]
  edges: Edge[];
  isDirty: boolean;
  /** Optional rules attached to the currently-loaded template (suggestions-only). */
  activeTemplateRules: unknown | null;
  /** The original rules from the loaded template (used for reset). */
  defaultTemplateRules: unknown | null;
  setActiveTemplateRules: (rules: unknown | null) => void;
  setDefaultTemplateRules: (rules: unknown | null) => void;
  resetActiveTemplateRulesToDefault: () => void;
  clearTemplateRules: () => void;
  onNodesChange: OnNodesChange<Node<NodeData>>;
  onEdgesChange: OnEdgesChange;
  updateNodeLabel: (nodeId: string, label: string) => void;
  updateNodeTask: (nodeId: string, isTask: boolean, label: string) => void;
  // updateNodeDescription: (nodeId: string, description: string) => void;
  updatePresentationOrder: (nodeId: string, presentationOrder: string) => void;
  updateNodeData: (nodeId: string, data: Partial<NodeData>) => void;
  addChildNode: (parentNode: Node<NodeData>, position: XYPosition) => string;
  updateParentNode: (
    childNodeId: string,
    newParentNodeId: string,
    childNode: Node<NodeData>,
    newParentNode: Node<NodeData>,
  ) => void;
  setOneNotePages: (oneNotePages: OneNotePage[]) => void;
  setNodes: (nodes: Node<NodeData>[]) => void;
  getNode: (nodeId: string) => Node<NodeData> | undefined;
  setEdges: (edges: Edge[]) => void;
  resetStore: () => void;
  updateNodeNotes: (
    nodeId: string,
    notes: string,
    links: { title: string; url: string }[],
  ) => void;
  selectedNodeId: string | null;
  setSelectedNodeId: (nodeId: string | null) => void;
  selectedNode: Node<NodeData> | null;
  setSelectedNode: (node: Node<NodeData>) => void;
  noteDetailsNodeId: string | null;
  setNoteDetailsNodeId: (nodeId: string | null) => void;
  focusValue: string;
  setFocusValue: (value: string) => void;
  showValues: boolean;
  setShowValues: (value: boolean) => void;
  updateValues: string;
  setUpdateValues: (value: string) => void;
  snapToGrid: boolean;
  toggleSnapToGrid: () => void;
  showMiniMap: boolean;
  toggleShowMiniMap: () => void;
  updateNodeLabelWithEmoji: (
    nodeId: string,
    emoji: string,
    position: number,
  ) => void;
  hideChildNodes: (nodeId: string) => void;
  showChildNodes: (nodeId: string, showAllLevels: boolean) => void;
  hideChildNode: (nodeId: string) => void;
  showChildNode: (nodeId: string) => void;
  addEdge: (edge: Edge) => void;
  setIsDirty: (isDirty: boolean) => void;
  addCrossLine: (sourceId: string, targetId: string) => void;
  selectedEdgeId: string | null;
  setSelectedEdgeId: (edgeId: string | null) => void;
  isPresentationMode: boolean;
  setIsPresentationMode: (isPresentationMode: boolean) => void;
  setEdgeLabel: (edgeId: string, label: string) => void;
  showNotesModal: boolean;
  setShowNotesModal: (show: boolean) => void;
  currentNotes: string | "";
  setCurrentNotes: (notes: string) => void;
  aiResponse: string | "";
  setAIResponse: (response: string) => void;
  currentLinks: { title: string; url: string }[];
  setCurrentLinks: (links: { title: string; url: string }[]) => void;
  currentNodeLabel: string;
  setCurrentNodeLabel: (label: string) => void;
  currentView: any;
  setCurrentView: (view: any) => void;
  updatePriority: (nodeId: string, priority: string) => void;
  updateValue: (nodeId: string, value: string) => void;
  calculateSummary: (nodeId: string) => string;
  updateNodeCustomProperties: (
    nodeId: string,
    customProperties: CustomProperty[],
  ) => void;
  updateNodeRollupValues: (
    nodeId: string,
    customProperties: CustomProperty[],
  ) => void;
  useLocalStorage: boolean;
  setUseLocalStorage: (useLocalStorage: boolean) => void;
  presentWithShowHideButtons: boolean;
  setPresentWithShowHideButtons: (presentWithShowHideButtons: boolean) => void;
  sourceOptions: string[];
  setSourceOptions: (sourceOptions: string[]) => void;
  doRollup: boolean;
  setDoRollup: (active: boolean) => void;
  currentFlowId: string | "";
  setCurrentFlowId: (flowId: string) => void;
  showViewSelector: boolean;
  setShowViewSelector: (show: boolean) => void;
  ideationMode: boolean;
  setIdeationMode: (mode: boolean) => void;
  ms365Mode: boolean;
  setMS365Mode: (mode: boolean) => void;
  aiMode: boolean;
  setAIMode: (mode: boolean) => void;
  detailsMode: boolean;
  setDetailsMode: (mode: boolean) => void;
  focusFilterStatuses: string[];
  setFocusFilterStatuses: (statuses: string[]) => void;
  currency: string;
  setCurrency: (currency: string) => void;
  tabModeActive: boolean;
  setTabModeActive: (active: boolean) => void;
  tabModeParentId: string | null;
  setTabModeParentId: (parentId: string | null) => void;
  addChildNodeInTabMode: (parentId: string) => string;
  redistributeChildNodes: (parentId: string) => void;
  redistributeAllChildNodes: (startNodeId: string) => void;
  redistributeAllChildNodesInStarLayout: (startNodeId: string) => void;
  redistributeAllChildNodesRadial: (startNodeId: string) => void;
  scaleChildNodePositions: (parentId: string, scaleFactor: number) => void;
  scaleAllChildNodePositions: (
    startNodeId: string,
    scaleFactor: number,
  ) => void;
  childNodeDistance: number;
  increaseChildNodeDistance: () => void;
  decreaseChildNodeDistance: () => void;
  increaseGlobalNodeDistance: () => void;
  decreaseGlobalNodeDistance: () => void;
  user: User | null;
  setUser: (user: User | null) => void;
  defaultNodeType: "nodeItem" | "noteItem";
  setDefaultNodeType: (type: "nodeItem" | "noteItem") => void;
  toastMessage: { severity: string; summary: string; detail: string } | null;
  showToast: (severity: string, summary: string, detail: string) => void;
  clearToast: () => void;
  // Drill-down tabs
  drilldownTabs: Array<{
    id: string;
    nodeId: string;
    label: string;
    parentTabId?: string | null;
  }>;
  activeDrilldownTab: string | null; // null = main view, otherwise tab id
  pendingFocusNodeId: string | null;
  setPendingFocusNodeId: (nodeId: string | null) => void;
  openDrilldownTab: (nodeId: string) => void;
  closeDrilldownTab: (tabId: string) => void;
  setActiveDrilldownTab: (tabId: string | null) => void;
  reorderDrilldownTabs: (fromIndex: number, toIndex: number) => void;
};

const recalculateRollup = async () => {
  const { nodes, setNodes } = useStore.getState();
  const updatedNodes = nodes.map((node) => ({
    ...node,
    data: { ...node.data, rollupValues: [] },
  }));
  setNodes(updatedNodes);

  const calculateRollup = () => {
    // Build adjacency list for parent-child relationships
    const adjacencyList = nodes.reduce(
      (acc, node) => {
        if (node.parentId) {
          if (!acc[node.parentId]) acc[node.parentId] = [];
          acc[node.parentId].push(node.id);
        }
        return acc;
      },
      {} as Record<string, string[]>,
    );

    // Create a map to store rollup values
    const rollupMap = new Map<string, values[]>();

    // Initialize rollup values for nodes without children
    const initializeRollupValues = (node: Node<NodeData>) => {
      const rollupValues: values[] = [];
      const customProps = node.data.customProperties;

      // console.log('initialize:', node.data.label);

      if (customProps) {
        customProps.forEach((prop: CustomProperty) => {
          if (
            prop.visible &&
            ["number", "string", "boolean", "date"].includes(prop.type)
          ) {
            rollupValues.push({ title: prop.name, value: prop.value });
          }
          // Handle currency type
          if (
            prop.visible &&
            prop.type === "currency" &&
            prop.value &&
            prop.value !== "" &&
            prop.value !== "0"
          ) {
            const currency = useStore.getState().currency || "EUR";
            rollupValues.push({
              title: prop.name,
              value: `${prop.value} ${currency}`,
            });
          }
          // Usage
          if (prop.visible && prop.type === "calculated") {
            const currency = useStore.getState().currency || "EUR";
            const rollupValue = getCalculatedValue(prop, customProps, currency);
            if (rollupValue) {
              rollupValues.push(rollupValue);
            }
          }
        });
      }

      rollupMap.set(node.id, rollupValues);
    };

    // Recursive function to calculate rollup values for a node
    const calculateNodeRollup = (nodeId: string): values[] => {
      if (rollupMap.has(nodeId)) {
        return rollupMap.get(nodeId) || [];
      }

      const children = adjacencyList[nodeId] || [];
      const aggregatedRollup: Record<string, number> = {};
      const currencyProperties = new Set<string>();

      // Aggregate rollup values from child nodes
      children.forEach((childId) => {
        const childRollup = calculateNodeRollup(childId);
        childRollup.forEach((rollup) => {
          if (!aggregatedRollup[rollup.title]) {
            aggregatedRollup[rollup.title] = 0;
          }
          // Check if this property has a currency suffix
          const valueStr = rollup.value || "0";
          if (
            valueStr.includes(" EUR") ||
            valueStr.includes(" USD") ||
            valueStr.includes(" GBP") ||
            valueStr.match(/\s[A-Z]{3}(\s|Σ|$)/)
          ) {
            currencyProperties.add(rollup.title);
          }
          aggregatedRollup[rollup.title] += parseFloat(rollup.value || "0");
        });
      });

      // Add custom properties to rollup values
      const nodeMap = useStore.getState().nodeMap;
      const node = nodeMap.get(nodeId);
      const rollupValues: values[] = [];
      if (node) {
        const currency = useStore.getState().currency || "EUR";
        Object.entries(aggregatedRollup).forEach(([title, aggregatedValue]) => {
          const isCurrency = currencyProperties.has(title);
          rollupValues.push({
            title,
            value: isCurrency
              ? `${aggregatedValue} ${currency} Σ`
              : aggregatedValue.toString() + " Σ",
          });
        });
      }

      rollupMap.set(nodeId, rollupValues);
      return rollupValues;
    };

    // Start by initializing rollup values for nodes without children
    nodes.forEach((node) => {
      if (!adjacencyList[node.id]) {
        initializeRollupValues(node);
      }
    });

    // Calculate rollup values for all nodes
    nodes.forEach((node) => {
      calculateNodeRollup(node.id);
    });

    // Update nodes with calculated rollup values
    const updatedNodes: Node<NodeData>[] = nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        rollupValues: rollupMap.get(node.id) || [],
      },
    }));

    setNodes(updatedNodes);
  };

  calculateRollup();
};

// Debounced version of recalculateRollup for performance optimization
const debouncedRecalculateRollup = () => {
  debounce(() => {
    recalculateRollup();
  }, 300); // 300ms delay
};

// Helper function to build childrenMap for O(1) child lookups
const buildChildrenMap = (nodes: Node<NodeData>[]): Map<string, string[]> => {
  const childrenMap = new Map<string, string[]>();
  nodes.forEach((node) => {
    if (node.parentId) {
      const children = childrenMap.get(node.parentId) || [];
      children.push(node.id);
      childrenMap.set(node.parentId, children);
    }
  });
  return childrenMap;
};

const useStore = createWithEqualityFn<RFState>((set, get) => ({
  oneNotePages: [],
  nodes: [],
  nodeMap: new Map(),
  childrenMap: new Map(),
  edges: [],
  isDirty: false,
  activeTemplateRules: null,
  defaultTemplateRules: null,
  setActiveTemplateRules: (rules) => set({ activeTemplateRules: rules }),
  setDefaultTemplateRules: (rules) => set({ defaultTemplateRules: rules }),
  resetActiveTemplateRulesToDefault: () =>
    set({ activeTemplateRules: get().defaultTemplateRules }),
  clearTemplateRules: () =>
    set({ activeTemplateRules: null, defaultTemplateRules: null }),
  onNodesChange: (changes: NodeChange<Node<NodeData>>[]) => {
    // Filter out any attempts to delete the "root" node
    let filteredChanges = changes.filter(
      (change) => !(change.type === "remove" && change.id === "root"),
    );

    if (filteredChanges.some((change) => change.type === "remove")) {
      const { edges, nodes } = get();

      // Build adjacency list for edges
      const adjacencyList = edges.reduce(
        (acc, edge) => {
          if (!acc[edge.source]) acc[edge.source] = [];
          acc[edge.source].push(edge.target);
          return acc;
        },
        {} as Record<string, string[]>,
      );

      // Helper function to find all descendants of a node
      const findDescendants = (nodeId: string): string[] => {
        const descendants: string[] = [];
        const queue: string[] = [nodeId]; // Start with the parent node

        while (queue.length > 0) {
          const current = queue.shift()!;
          const children = adjacencyList[current] || [];
          descendants.push(current);
          queue.push(...children);
        }

        return descendants;
      };

      // Collect all nodes to delete
      const nodesToDelete = new Set<string>();
      filteredChanges
        .filter((change) => change.type === "remove")
        .forEach((change) => {
          const descendants = findDescendants((change as { id: string }).id);
          descendants.forEach((descendant) => nodesToDelete.add(descendant));
        });

      // Filter out nodes and edges to be deleted
      const remainingNodes = nodes.filter(
        (node) => !nodesToDelete.has(node.id),
      );
      const remainingEdges = edges.filter(
        (edge) =>
          !nodesToDelete.has(edge.source) && !nodesToDelete.has(edge.target),
      );

      // Update the state with batched changes including nodeMap and childrenMap
      const nodeMap = new Map(remainingNodes.map((node) => [node.id, node]));
      const childrenMap = buildChildrenMap(remainingNodes);
      set((state) => ({
        nodes: remainingNodes,
        nodeMap,
        childrenMap,
        edges: remainingEdges,
        isDirty: true,
      }));

      // Trigger rollup asynchronously after state update (node deletion affects rollups)
      if (get().doRollup) {
        debouncedRecalculateRollup();
      }
    } else {
      // Apply changes if no node deletions are involved
      filteredChanges = changes.filter((change) => change.type !== "remove");

      // Persist resize dimensions by syncing them into node.width/node.height.
      // XYFlow may keep measurements in `measured`, but we want stable width/height in saved nodes.
      const dimensionById = new Map<
        string,
        { width: number; height: number }
      >();
      filteredChanges.forEach((change) => {
        if (change.type === "dimensions") {
          const dims = (change as any).dimensions as
            | { width: number; height: number }
            | undefined;
          if (
            dims &&
            typeof dims.width === "number" &&
            typeof dims.height === "number"
          ) {
            dimensionById.set((change as any).id, dims);
          }
        }
      });

      const updatedNodes = applyNodeChanges(
        filteredChanges,
        get().nodes,
      ) as Node<NodeData>[];

      const updatedNodesWithPersistedDimensions =
        dimensionById.size === 0
          ? updatedNodes
          : updatedNodes.map((node) => {
              const dims = dimensionById.get(node.id);
              if (!dims) return node;
              return {
                ...node,
                width: dims.width,
                height: dims.height,
              };
            });

      // Check if changes are only position updates (during drag)
      const onlyPositionChanges = filteredChanges.every(
        (change) => change.type === "position",
      );

      // For position-only changes, update nodes array but skip expensive map rebuilds
      if (onlyPositionChanges) {
        set({ nodes: updatedNodesWithPersistedDimensions });
        return;
      }

      // For non-position changes, do full update including maps
      const nodeMap = new Map(
        updatedNodesWithPersistedDimensions.map((node) => [node.id, node]),
      );
      const childrenMap = buildChildrenMap(updatedNodesWithPersistedDimensions);

      set({
        nodes: updatedNodesWithPersistedDimensions,
        nodeMap,
        childrenMap,
        isDirty: true,
      });

      // Only trigger rollup if enabled AND changes affect rollup data
      // Skip for position/dimension changes as they don't affect rollup values
      if (get().doRollup) {
        const affectsRollup = filteredChanges.some((change) => {
          // Position and dimension changes don't affect rollup calculations
          if (change.type === "position" || change.type === "dimensions") {
            return false;
          }
          // Other changes (add, remove, select, data updates) may affect rollups
          return true;
        });

        if (affectsRollup) {
          // console.log("🔄 Triggering rollup recalculation");
          debouncedRecalculateRollup();
        }
      }
    }
  },
  onEdgesChange: (changes: EdgeChange[]) => {
    // Filter out any remove changes to prevent edge deletion
    // console.log(changes);
    const filteredChanges = changes.filter((change) => {
      const edge = get().edges.find(
        (e) => e.id === (change as { id: string }).id,
      );
      //alert(edge?.type);
      return change.type !== "remove" || edge?.type === "crossline";
    });

    // Batch state updates
    set((state) => ({
      edges: applyEdgeChanges(filteredChanges, get().edges),
      isDirty: true,
    }));
  },
  updateNodeLabel: (nodeId: string, label: string) => {
    // Sanitize HTML to only allow safe formatting
    const sanitizedLabel = sanitizeLabel(label);
    set(
      produce((state: RFState) => {
        const node = state.nodeMap.get(nodeId);
        if (node) {
          node.data.label = sanitizedLabel;
          // Also update in nodes array
          const nodeIndex = state.nodes.findIndex((n) => n.id === nodeId);
          if (nodeIndex !== -1) {
            state.nodes[nodeIndex] = node;
          }
        }
      }),
    );
  },
  updateNodeTask: (nodeId: string, isTask: boolean, label: string) => {
    let task: Task | undefined = {
      id: nodeId,
      nodeTitle: label,
      title: label,
      status: "none",
      summary: "",
      priority: "Normal",
      tags: "",
      assignee: "",
      estimate: 0,
      rankId: 0,
      color: "",
      className: "",
      startDate: undefined,
      endDate: undefined,
    };
    if (!isTask) {
      task = undefined;
    }

    set(
      produce((state: RFState) => {
        const node = state.nodeMap.get(nodeId);
        if (node) {
          // Update the node data with the task
          node.data = { ...node.data, task };
          // Update the node in the array to ensure reference change
          const nodeIndex = state.nodes.findIndex((n) => n.id === nodeId);
          if (nodeIndex !== -1) {
            state.nodes[nodeIndex] = { ...node };
          }
        }
      }),
    );
  },
  // updateNodeDescription: (nodeId: string, description: string) => {
  //   set((state) => ({
  //     nodes: get().nodes.map((node) => {
  //       if (node.id === nodeId) {
  //         node.data = { ...node.data, description };
  //       }
  //       return node;
  //     }),
  //   }));
  // },
  updatePresentationOrder: (nodeId: string, presentationOrder: string) => {
    set(
      produce((state: RFState) => {
        const node = state.nodeMap.get(nodeId);
        if (node) {
          node.data.presentationOrder = presentationOrder;
          // Update the node in the nodes array as well
          const nodeIndex = state.nodes.findIndex((n) => n.id === nodeId);
          if (nodeIndex !== -1) {
            state.nodes[nodeIndex] = { ...node };
          }
        }
      }),
    );
  },
  updateNodeData: (nodeId: string, data: Partial<NodeData>) => {
    set(
      produce((state: RFState) => {
        const node = state.nodeMap.get(nodeId);
        if (node) {
          // Merge the new data with existing data
          node.data = { ...node.data, ...data };
          // Update the node in the array as well to ensure reference change
          const nodeIndex = state.nodes.findIndex((n) => n.id === nodeId);
          if (nodeIndex !== -1) {
            state.nodes[nodeIndex] = { ...node };
          }
        }
      }),
    );
  },
  updateNodeLabelWithEmoji: (
    nodeId: string,
    emoji: string,
    position: number,
  ) => {
    set(
      produce((state: RFState) => {
        const node = state.nodeMap.get(nodeId);
        if (node) {
          const newLabel =
            node.data.label.slice(0, position) +
            emoji +
            node.data.label.slice(position);
          node.data.label = newLabel;
        }
      }),
    );
  },
  addChildNode: (parentNode: Node<NodeData>, position: XYPosition) => {
    const newNodeId = nanoid();
    const state = get();
    const defaultType = state.defaultNodeType;

    // If parent is noteItem, child should be noteItem
    // Otherwise use the user's default preference
    const newNodeType =
      parentNode.type === "noteItem" ? "noteItem" : defaultType;

    const newNode: Node<NodeData> =
      newNodeType === "noteItem"
        ? {
            id: newNodeId,
            type: "noteItem",
            dragging: false,
            selected: false,
            data: {
              label: "",
              icon: "",
              object: "",
              objectType: "note",
              objectWidth: 320,
              objectHeight: 220,
              notes: "<p></p>",
              autoTitleEdit: true,
              complete: false,
              status: "none",
              flagged: "none",
              color: parentNode.data.color,
              backgroundColor: parentNode.data.backgroundColor,
              presentationOrder: "",
              priority: "",
              links: [],
              value: "0",
              values: [],
              rollupValues: [],
              customProperties: [],
              event: {
                id: "",
                nodeTitle: "",
                title: "",
                description: "",
                date: undefined,
                icon: "",
                color: "",
              },
              address: {
                id: "",
                nodeTitle: "",
                name: "",
                street: "",
                zip_code: "",
                city: "",
                Phone: "",
                email: "",
                vat: "",
                employees: "",
                notes: "",
                latitude: undefined,
                longitude: undefined,
              },
              task: {
                id: "",
                nodeTitle: "",
                title: "",
                status: "",
                summary: "",
                priority: "",
                tags: "",
                assignee: "",
                estimate: 0,
                rankId: 0,
                color: "",
                className: "",
                startDate: undefined,
                endDate: undefined,
              },
              ms365Document: null,
            },
            position,
            width: 160,
            height: 70,
            dragHandle: ".noteItem-dot",
            parentId: parentNode.id,
          }
        : {
            id: newNodeId,
            type: "mindmap",
            dragging: false,
            selected: false,
            data: {
              label: "Point",
              icon: "",
              object: "",
              objectType: "text",
              objectWidth: 50,
              objectHeight: 18,
              notes: "",
              complete: false,
              status: "none",
              flagged: "none",
              color: parentNode.data.color,
              backgroundColor: parentNode.data.backgroundColor,
              presentationOrder: "",
              priority: "",
              links: [],
              value: "0",
              values: [],
              rollupValues: [],
              customProperties: [],
              event: {
                id: "",
                nodeTitle: "",
                title: "",
                description: "",
                date: undefined,
                icon: "",
                color: "",
              },
              address: {
                id: "",
                nodeTitle: "",
                name: "",
                street: "",
                zip_code: "",
                city: "",
                Phone: "",
                email: "",
                vat: "",
                employees: "",
                notes: "",
                latitude: undefined,
                longitude: undefined,
              },
              task: {
                id: "",
                nodeTitle: "",
                title: "",
                status: "",
                summary: "",
                priority: "",
                tags: "",
                assignee: "",
                estimate: 0,
                rankId: 0,
                color: "",
                className: "",
                startDate: undefined,
                endDate: undefined,
              },
              ms365Document: null,
            },
            position,
            width: 50,
            height: 18,
            dragHandle: ".dragHandle",
            parentId: parentNode.id,
            style: {
              backgroundColor: parentNode.data.backgroundColor,
            },
          };

    const newEdge = {
      id: nanoid(),
      type:
        parentNode.id === "root" || parentNode.type === "center"
          ? "center"
          : "mindmap",
      source: parentNode.id,
      target: newNode.id,
    };

    // Mark the new node as selected
    newNode.selected = true;

    const updatedNodes = [...get().nodes, newNode];
    const orderedNodes = orderNodesParentBeforeChild(updatedNodes);
    const updatedEdges = [...get().edges, newEdge];
    const nodeMap = new Map(orderedNodes.map((node) => [node.id, node]));
    const childrenMap = buildChildrenMap(orderedNodes);

    set({
      nodes: orderedNodes,
      edges: updatedEdges,
      nodeMap,
      childrenMap,
      selectedNode: newNode,
      selectedNodeId: newNode.id,
    });

    return newNode.id;
  },
  updateParentNode: (
    childNodeId: string,
    newParentNodeId: string,
    childNode: Node<NodeData>,
    newParentNode: Node<NodeData>,
  ) => {
    const { nodes, edges } = get();

    // Remove the old edge
    const updatedEdges = edges.filter((edge) => edge.target !== childNodeId);

    // Add the new edge
    const newEdge: Edge = {
      id: nanoid(),
      type:
        newParentNodeId === "root" || newParentNode.type === "center"
          ? "center"
          : "mindmap",
      source: newParentNodeId,
      target: childNodeId,
    };

    // Convert absolute -> relative while keeping the node visually stationary.
    // With nodeOrigin=[0.5,0.5], positions represent the node's center, while
    // internals.positionAbsolute represents the top-left.
    const childAbsTopLeft = getNodeAbsTopLeft(childNode);
    const parentAbsTopLeft = getNodeAbsTopLeft(newParentNode);
    const childSize = getNodeSize(childNode);

    const childAbsOrigin = {
      x: childAbsTopLeft.x + childSize.width * NODE_ORIGIN.x,
      y: childAbsTopLeft.y + childSize.height * NODE_ORIGIN.y,
    };

    const relativePosition = {
      x: childAbsOrigin.x - parentAbsTopLeft.x,
      y: childAbsOrigin.y - parentAbsTopLeft.y,
    };

    // Update parentId and position (relative to new parent)
    const updatedNodes = nodes.map((node) => {
      if (node.id === childNodeId) {
        return {
          ...node,
          parentId: newParentNodeId,
          position: relativePosition,
          data: {
            label: childNode.data.label,
            icon: childNode.data.icon,
            object: childNode.data.object,
            objectType: childNode.data.objectType,
            objectWidth: childNode.data.objectWidth || 0,
            objectHeight: childNode.data.objectHeight || 0,
            notes: childNode.data.notes,
            complete: childNode.data.complete,
            status: childNode.data.status || "none",
            flagged: childNode.data.flagged,
            color: newParentNode.data.color,
            backgroundColor: newParentNode.data.backgroundColor,
            links: childNode.data.links || [],
            priority: childNode.data.priority || "",
            presentationOrder: childNode.data.presentationOrder || "",
            value: childNode.data.value || "0",
            values: childNode.data.values || [],
            rollupValues: childNode.data.rollupValues || [],
            customProperties: childNode.data.customProperties || [],
            event: childNode.data.event,
            address: childNode.data.address,
            task: childNode.data.task,
            ms365Document: childNode.data.ms365Document,
          },
          style: {
            backgroundColor: newParentNode.data.backgroundColor,
          },
        };
      }
      return node;
    });

    const orderedNodes = orderNodesParentBeforeChild(updatedNodes);
    const nodeMap = new Map(orderedNodes.map((node) => [node.id, node]));
    const childrenMap = buildChildrenMap(orderedNodes);

    set(() => ({
      nodes: orderedNodes,
      nodeMap,
      childrenMap,
      edges: [...updatedEdges, newEdge],
    }));

    debouncedRecalculateRollup();
  },
  setOneNotePages: (oneNotePages: OneNotePage[]) => set({ oneNotePages }),
  setNodes: (nodes: Node<NodeData>[]) => {
    const orderedNodes = orderNodesParentBeforeChild(nodes);
    const nodeMap = new Map(orderedNodes.map((node) => [node.id, node]));
    const childrenMap = buildChildrenMap(orderedNodes);
    set({ nodes: orderedNodes, nodeMap, childrenMap });
  },
  getNode: (nodeId: string) => get().nodeMap.get(nodeId),
  setEdges: (edges: Edge[]) => set({ edges }),
  resetStore: () => {
    set({
      nodes: [],
      nodeMap: new Map(),
      childrenMap: new Map(),
      edges: [],
      isDirty: false, // prevent stale auto-save from firing after a reset
    });
  },
  updateNodeNotes: (
    nodeId: string,
    notes: string,
    links: { title: string; url: string }[],
  ) => {
    set((state) => ({
      nodes: state.nodes.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, notes, links } }
          : node,
      ),
    }));
  },
  selectedNodeId: null,
  setSelectedNodeId: (nodeId: string | null) =>
    set((state) => ({
      selectedNodeId: nodeId,
      nodes: state.nodes.map((node) => ({
        ...node,
        selected: node.id === nodeId,
      })),
    })),
  selectedNode: null,
  setSelectedNode: (node: Node<NodeData> | null) => set({ selectedNode: node }),
  noteDetailsNodeId: null,
  setNoteDetailsNodeId: (nodeId: string | null) =>
    set({ noteDetailsNodeId: nodeId }),
  focusValue: "",
  setFocusValue: (value: string) => set({ focusValue: value }),
  showValues: false,
  setShowValues: (value: boolean) => set({ showValues: value }),
  updateValues: "",
  setUpdateValues: (value: string) => set({ updateValues: value }),
  snapToGrid: false,
  toggleSnapToGrid: () => set((state) => ({ snapToGrid: !state.snapToGrid })),
  showMiniMap: false,
  toggleShowMiniMap: () =>
    set((state) => ({ showMiniMap: !state.showMiniMap })),
  hideChildNodes: (nodeId: string) => {
    const { nodes } = get();
    const hideDescendants = (
      parentId: string,
      nodesToHide: Set<string>,
    ): void => {
      nodes.forEach((node) => {
        if (node.parentId === parentId) {
          nodesToHide.add(node.id);
          hideDescendants(node.id, nodesToHide);
        }
      });
    };

    const nodesToHide = new Set<string>();
    hideDescendants(nodeId, nodesToHide);

    const updatedNodes = nodes.map((node) => {
      if (nodesToHide.has(node.id)) {
        return { ...node, hidden: true };
      }
      return node;
    });

    get().setNodes(updatedNodes);
  },
  showChildNode: (nodeId: string) => {
    const { nodes } = get();
    const updatedNodes = nodes.map((node) => {
      if (node.id === nodeId) {
        node.hidden = false;
      }
      return node;
    });
    get().setNodes(updatedNodes);
  },
  hideChildNode: (nodeId: string) => {
    const { nodes } = get();
    const updatedNodes = nodes.map((node) => {
      if (node.id === nodeId) {
        if (node.type !== "center") {
          node.hidden = true;
        }
      }
      return node;
    });
    get().setNodes(updatedNodes);
  },
  showChildNodes: (nodeId: string, showAllLevels: boolean = false) => {
    const { nodes } = get();

    // Collect all node IDs to show (direct children, or all descendants)
    const showSet = new Set<string>();
    const collectChildren = (parentId: string) => {
      nodes.forEach((node) => {
        if (node.parentId === parentId) {
          showSet.add(node.id);
          if (showAllLevels) collectChildren(node.id);
        }
      });
    };
    collectChildren(nodeId);

    if (showSet.size === 0) return;

    // Create NEW node objects (like hideChildNodes does) so React detects the change
    const updatedNodes = nodes.map((node) =>
      showSet.has(node.id) ? { ...node, hidden: false } : node,
    );
    get().setNodes(updatedNodes);
    setTimeout(() => {
      document.getSelection()?.removeAllRanges();
    }, 50);
  },
  addEdge: (edge: Edge) => {
    set((state) => ({
      edges: [...state.edges, edge],
    }));
  },
  setIsDirty: (isDirty: boolean) => set({ isDirty }),
  addCrossLine: (sourceId: string, targetId: string) => {
    const newEdge: Edge = {
      id: nanoid(),
      type: "crossline",
      source: sourceId,
      target: targetId,
      label: "",
    };
    set((state) => ({
      edges: [...state.edges, newEdge],
    }));
  },
  selectedEdgeId: null,
  setSelectedEdgeId: (edgeId: string | null) => set({ selectedEdgeId: edgeId }),
  isPresentationMode: false,
  setIsPresentationMode: (isPresentationMode: boolean) =>
    set({ isPresentationMode }),
  presentWithShowHideButtons: false,
  setPresentWithShowHideButtons: (presentWithShowHideButtons: boolean) =>
    set({ presentWithShowHideButtons }),
  setEdgeLabel: (edgeId: string, label: string) => {
    set((state) => ({
      edges: state.edges.map((edge) =>
        edge.id === edgeId ? { ...edge, label } : edge,
      ),
    }));
  },
  currentNotes: "",
  setCurrentNotes: (notes: string) => set({ currentNotes: notes }),
  aiResponse: "",
  setAIResponse: (response: string) => set({ aiResponse: response }),
  currentLinks: [],
  setCurrentLinks: (links: { title: string; url: string }[]) =>
    set({ currentLinks: links }),
  currentView: viewTypes.FocusMap,
  setCurrentView: (view: any) => set({ currentView: view }),
  showNotesModal: false,
  setShowNotesModal: (show: boolean) => set({ showNotesModal: show }),
  updatePriority: (nodeId: string, priority: string) => {
    set((state) => ({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId) {
          node.data = { ...node.data, priority };
        }
        return node;
      }),
    }));
  },
  updateValue: (nodeId: string, value: string) => {
    set((state) => ({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId) {
          node.data = { ...node.data, value };
        }
        return node;
      }),
    }));
    debouncedRecalculateRollup();
  },
  calculateSummary: (nodeId: string) => {
    const { nodes } = get();
    const calculateSum = (nodeId: string): number => {
      const adjacencyList = nodes.reduce(
        (acc, node) => {
          if (node.parentId && !acc[node.parentId]) acc[node.parentId] = [];
          if (node.parentId) {
            acc[node.parentId].push(node.id);
          }
          return acc;
        },
        {} as Record<string, string[]>,
      );

      const findDescendants = (nodeId: string): string[] => {
        const descendants: string[] = [];
        const queue: string[] = [nodeId];

        while (queue.length > 0) {
          const current = queue.shift()!;
          const children = adjacencyList[current] || [];
          descendants.push(current);
          queue.push(...children);
        }

        return descendants;
      };

      const descendants = findDescendants(nodeId);
      const nodeMap = get().nodeMap;
      const sum = descendants.reduce((acc, id) => {
        const node = nodeMap.get(id);
        return acc + (node?.data.value ? parseFloat(node.data.value) : 0);
      }, 0);

      return sum;
    };

    const sum = calculateSum(nodeId);
    console.log(`Sum of values for node ${nodeId} and its descendants: ${sum}`);
    return sum.toString();
  },
  updateNodeCustomProperties: (
    nodeId: string,
    customProperties: CustomProperty[],
  ) => {
    set((state) => ({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId) {
          node.data = {
            ...node.data,
            customProperties,
            values: customProperties
              .map((prop: CustomProperty) => {
                if (prop.visible) {
                  if (prop.type === "calculated") {
                    const currency = useStore.getState().currency || "EUR";
                    const value = getCalculatedValue(
                      prop,
                      customProperties,
                      currency,
                    );
                    return value || { title: prop.name, value: "0" };
                  } else {
                    // Only return values that are actually set (non-empty)
                    const hasValue =
                      prop.type === "boolean"
                        ? true // boolean always has a value (true/false)
                        : prop.value !== "" &&
                          prop.value !== null &&
                          prop.value !== undefined;

                    if (hasValue) {
                      let displayValue = prop.value;
                      // Add currency suffix for currency type properties
                      if (prop.type === "currency") {
                        const currency = useStore.getState().currency || "EUR";
                        displayValue = `${prop.value} ${currency}`;
                      }
                      return { title: prop.name, value: displayValue };
                    } else {
                      return { title: prop.name, value: "0" };
                    }
                  }
                } else {
                  return { title: prop.name, value: "0" };
                }
              })
              .filter((value: values) => value.value !== "0") as values[] | [],
          };
        }
        return node;
      }),
    }));
    //get().setUpdateValues(nanoid());
  },
  updateNodeRollupValues: (
    nodeId: string,
    customProperties: CustomProperty[],
  ) => {
    set((state) => ({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId) {
          const rollupValues: values[] = [];
          customProperties.forEach((prop) => {
            if (prop.value) {
              rollupValues.push({ title: prop.name, value: prop.value });
              // }
            }
          });
          node.data = { ...node.data, rollupValues };
        }
        return node;
      }),
    }));
  },
  useLocalStorage: false,
  setUseLocalStorage: (useLocal: boolean) => set({ useLocalStorage: useLocal }),
  sourceOptions: [],
  setSourceOptions: (sourceOptions: string[]) => set({ sourceOptions }),
  doRollup: false,
  setDoRollup: (active: boolean) => {
    set({ doRollup: active });
    if (active) {
      recalculateRollup(); // Immediate calculation when user explicitly enables rollup
    }
  },
  currentFlowId: "",
  setCurrentFlowId: (flowId: string) => set({ currentFlowId: flowId }),
  showViewSelector: false,
  setShowViewSelector: (value: boolean) => set({ showViewSelector: value }),
  ideationMode: false,
  setIdeationMode: (value: boolean) => set({ ideationMode: value }),
  ms365Mode: false,
  setMS365Mode: (value: boolean) => set({ ms365Mode: value }),
  aiMode: false,
  setAIMode: (value: boolean) => set({ aiMode: value }),
  detailsMode: false,
  setDetailsMode: (value: boolean) => set({ detailsMode: value }),
  currentNodeLabel: "",
  setCurrentNodeLabel: (label: string) => set({ currentNodeLabel: label }),
  focusFilterStatuses: [],
  setFocusFilterStatuses: (statuses: string[]) =>
    set({ focusFilterStatuses: statuses }),
  currency: "EUR",
  setCurrency: (currency: string) => set({ currency }),
  tabModeActive: false,
  setTabModeActive: (active: boolean) => set({ tabModeActive: active }),
  tabModeParentId: null,
  setTabModeParentId: (parentId: string | null) =>
    set({ tabModeParentId: parentId }),
  childNodeDistance: 160,
  scaleChildNodePositions: (parentId: string, scaleFactor: number) => {
    const { nodes, setNodes } = get();
    const children = nodes.filter((n) => n.parentId === parentId);
    if (children.length === 0) return;

    const updatedNodes = nodes.map((node) => {
      if (node.parentId === parentId) {
        // Scale the position proportionally
        return {
          ...node,
          position: {
            x: node.position.x * scaleFactor,
            y: node.position.y * scaleFactor,
          },
        };
      }
      return node;
    });

    setNodes(updatedNodes);
  },
  scaleAllChildNodePositions: (startNodeId: string, scaleFactor: number) => {
    const { nodes, setNodes } = get();

    console.log("📐 scaleAllChildNodePositions called:", {
      startNodeId,
      scaleFactor,
      totalNodes: nodes.length,
    });

    // Build a map of parent to children
    const childrenMap = new Map<string, Node[]>();
    nodes.forEach((node) => {
      if (node.parentId) {
        if (!childrenMap.has(node.parentId)) {
          childrenMap.set(node.parentId, []);
        }
        childrenMap.get(node.parentId)!.push(node);
      }
    });

    console.log("📊 Children map built:", {
      directChildren: (childrenMap.get(startNodeId) || []).length,
    });

    // Collect all descendants to scale
    const nodesToScale = new Set<string>();
    const collectDescendants = (nodeId: string) => {
      const children = childrenMap.get(nodeId) || [];
      children.forEach((child) => {
        nodesToScale.add(child.id);
        collectDescendants(child.id);
      });
    };
    collectDescendants(startNodeId);

    console.log("🎯 Nodes to scale:", nodesToScale.size);

    // Scale all descendant positions
    const updatedNodes = nodes.map((node) => {
      if (nodesToScale.has(node.id)) {
        const oldPos = node.position;
        const newPos = {
          x: node.position.x * scaleFactor,
          y: node.position.y * scaleFactor,
        };
        console.log(`  Scaling ${node.id}:`, {
          old: oldPos,
          new: newPos,
        });
        return {
          ...node,
          position: newPos,
        };
      }
      return node;
    });

    setNodes(updatedNodes);
  },
  increaseChildNodeDistance: () => {
    const currentDistance = get().childNodeDistance;
    set({ childNodeDistance: Math.min(currentDistance + 20, 400) });
  },
  decreaseChildNodeDistance: () => {
    const currentDistance = get().childNodeDistance;
    set({ childNodeDistance: Math.max(currentDistance - 20, 20) });
  },
  increaseGlobalNodeDistance: () => {
    const currentDistance = get().childNodeDistance;
    const newDistance = Math.min(currentDistance + 20, 400);
    set({ childNodeDistance: newDistance });

    // Re-layout all parent nodes
    const { nodes, redistributeAllChildNodes } = get();
    // Find all root nodes (nodes without parents)
    const rootNodes = nodes.filter((n) => !n.parentId);
    rootNodes.forEach((rootNode) => {
      redistributeAllChildNodes(rootNode.id);
    });
  },
  decreaseGlobalNodeDistance: () => {
    const currentDistance = get().childNodeDistance;
    const newDistance = Math.max(currentDistance - 20, 20);
    set({ childNodeDistance: newDistance });

    // Re-layout all parent nodes
    const { nodes, redistributeAllChildNodes } = get();
    // Find all root nodes (nodes without parents)
    const rootNodes = nodes.filter((n) => !n.parentId);
    rootNodes.forEach((rootNode) => {
      redistributeAllChildNodes(rootNode.id);
    });
  },
  addChildNodeInTabMode: (parentId: string) => {
    const { nodes, edges, addChildNode, childNodeDistance } = get();
    const parentNode = nodes.find((n) => n.id === parentId);
    if (!parentNode) return "";

    // Find children count to determine position
    const childrenCount = nodes.filter((n) => n.parentId === parentId).length;
    const angle = 270 + childrenCount * 60; // Start from top (270°), go clockwise
    const distance = childNodeDistance;
    const radians = (angle * Math.PI) / 180;

    // Get parent dimensions - prefer measured over initial
    const parentWidth =
      (parentNode as any).measured?.width || (parentNode as any).width || 100;
    const parentHeight =
      (parentNode as any).measured?.height || (parentNode as any).height || 40;

    // Child positions are relative to parent's top-left corner
    // Even with nodeOrigin [0.5, 0.5], child.position is relative to parent's top-left
    // So we need to add parentWidth/2, parentHeight/2 to center the circular layout
    const newPosition = {
      x: Math.cos(radians) * distance + parentWidth / 2,
      y: Math.sin(radians) * distance + parentHeight / 2,
    };

    const newNodeId = addChildNode(parentNode, newPosition);

    return newNodeId;
  },
  redistributeChildNodes: (parentId: string) => {
    const { nodes, setNodes, childNodeDistance } = get();
    const parentNode = nodes.find((n) => n.id === parentId);
    if (!parentNode) return;

    const children = nodes.filter((n) => n.parentId === parentId);
    if (children.length === 0) return;

    const distance = childNodeDistance;
    const angleStep = 360 / children.length;

    // Get parent dimensions
    const parentWidth =
      (parentNode as any).measured?.width || (parentNode as any).width || 100;
    const parentHeight =
      (parentNode as any).measured?.height || (parentNode as any).height || 40;

    const updatedNodes = nodes.map((node) => {
      const childIndex = children.findIndex((c) => c.id === node.id);
      if (childIndex === -1) return node;

      const angle = 270 + childIndex * angleStep; // Start from top (270°), go clockwise
      const radians = (angle * Math.PI) / 180;

      // Child positions are relative to parent's top-left corner
      // Even with nodeOrigin [0.5, 0.5], child.position is relative to parent's top-left
      // So we need to add parentWidth/2, parentHeight/2 to center the circular layout
      const relativePos = {
        x: Math.cos(radians) * distance + parentWidth / 2,
        y: Math.sin(radians) * distance + parentHeight / 2,
      };

      return {
        ...node,
        position: relativePos,
      };
    });

    setNodes(updatedNodes);
  },
  redistributeAllChildNodes: (startNodeId: string) => {
    const {
      nodes,
      setNodes,
      childNodeDistance,
      nodeMap,
      childrenMap: storeChildrenMap,
    } = get();

    // Find the starting node
    const startNode = nodeMap.get(startNodeId);
    if (!startNode) return;

    // Convert childrenMap from Map<string, string[]> to Map<string, Node[]>
    const childrenMap = new Map<string, Node[]>();
    storeChildrenMap.forEach((childIds, parentId) => {
      const childNodes = childIds
        .map((id) => nodeMap.get(id))
        .filter(Boolean) as Node[];
      if (childNodes.length > 0) {
        childrenMap.set(parentId, childNodes);
      }
    });

    // Recursively layout nodes starting from root
    const updatedPositions = new Map<string, { x: number; y: number }>();

    const layoutChildren = (parentId: string) => {
      const children = childrenMap.get(parentId);
      if (!children || children.length === 0) return;

      const parentNode = nodes.find((n) => n.id === parentId);
      if (!parentNode) return;

      const distance = childNodeDistance;
      const angleStep = 360 / children.length;

      // Get parent dimensions - prefer measured over initial
      const parentWidth =
        (parentNode as any).measured?.width || (parentNode as any).width || 100;
      const parentHeight =
        (parentNode as any).measured?.height ||
        (parentNode as any).height ||
        40;

      children.forEach((child, index) => {
        const angle = 270 + index * angleStep;
        const radians = (angle * Math.PI) / 180;

        const relativePos = {
          x: Math.cos(radians) * distance + parentWidth / 2,
          y: Math.sin(radians) * distance + parentHeight / 2,
        };

        updatedPositions.set(child.id, relativePos);

        // Recursively layout this child's children
        layoutChildren(child.id);
      });
    };

    // Start layout from the selected node
    layoutChildren(startNode.id);

    // Apply all position updates
    const updatedNodes = nodes.map((node) => {
      const newPos = updatedPositions.get(node.id);
      if (newPos) {
        return {
          ...node,
          position: newPos,
        };
      }
      return node;
    });

    // Use requestAnimationFrame to prevent browser freezing on rapid calls
    requestAnimationFrame(() => {
      setNodes(updatedNodes);
    });
  },
  redistributeAllChildNodesInStarLayout: (startNodeId: string) => {
    const {
      nodes,
      setNodes,
      childNodeDistance,
      nodeMap,
      childrenMap: storeChildrenMap,
    } = get();
    console.log(
      "🎯 Layout starting for node:",
      startNodeId,
      "- Total nodes:",
      nodes.length,
    );

    // Find the starting node
    const startNode = nodeMap.get(startNodeId);
    if (!startNode) {
      console.warn("⚠️ Start node not found:", startNodeId);
      return;
    }

    // Convert childrenMap from Map<string, string[]> to Map<string, Node[]>
    const childrenMap = new Map<string, Node[]>();
    storeChildrenMap.forEach((childIds, parentId) => {
      const childNodes = childIds
        .map((id) => nodeMap.get(id))
        .filter(Boolean) as Node[];
      if (childNodes.length > 0) {
        childrenMap.set(parentId, childNodes);
      }
    });

    console.log(
      "🎯 Children map built. Root has",
      (childrenMap.get(startNodeId) || []).length,
      "children",
    );

    // Calculate depth of each node for distance scaling
    const nodeDepths = new Map<string, number>();
    const calculateDepth = (nodeId: string, depth: number) => {
      nodeDepths.set(nodeId, depth);
      const children = childrenMap.get(nodeId) || [];
      children.forEach((child) => calculateDepth(child.id, depth + 1));
    };
    calculateDepth(startNodeId, 0);

    const updatedPositions = new Map<string, { x: number; y: number }>();

    // Hierarchical radial layout with sector allocation
    const layoutChildren = (
      parentId: string,
      parentAngle: number,
      angleSpan: number,
      depth: number,
      parentAbsolutePos: { x: number; y: number },
    ) => {
      const children = childrenMap.get(parentId);
      if (!children || children.length === 0) return;

      const parentNode = nodes.find((n) => n.id === parentId);
      if (!parentNode) return;

      // Moderate distance scaling for better readability
      const baseDistance = childNodeDistance;
      const distance = baseDistance * Math.pow(1.6, depth);
      const angleStep = angleSpan / children.length;

      children.forEach((child, index) => {
        // Distribute children within their allocated angle sector
        const startAngle = parentAngle - angleSpan / 2;
        const childAngle = startAngle + (index + 0.5) * angleStep;
        const radians = (childAngle * Math.PI) / 180;

        // Calculate absolute position from center (not relative to parent)
        const absolutePos = {
          x: Math.cos(radians) * distance,
          y: Math.sin(radians) * distance,
        };

        updatedPositions.set(child.id, absolutePos);

        // Recursively layout this child's children
        layoutChildren(child.id, childAngle, angleStep, depth + 1, absolutePos);
      });
    };

    // For root level, allocate full 360° divided among direct children
    const rootChildren = childrenMap.get(startNodeId);
    if (rootChildren && rootChildren.length > 0) {
      const rootAngleStep = 360 / rootChildren.length;

      rootChildren.forEach((child, index) => {
        const childAngle = 315 + index * rootAngleStep; // Start at 315° (1:30 position)
        const distance = childNodeDistance;

        const radians = (childAngle * Math.PI) / 180;
        const absolutePos = {
          x: Math.cos(radians) * distance,
          y: Math.sin(radians) * distance,
        };

        updatedPositions.set(child.id, absolutePos);

        // Layout grandchildren within this child's sector
        layoutChildren(child.id, childAngle, rootAngleStep, 1, absolutePos);
      });
    }

    // Apply all position updates
    const updatedNodes = nodes.map((node) => {
      const newPos = updatedPositions.get(node.id);
      if (newPos) {
        return {
          ...node,
          position: newPos,
        };
      }
      return node;
    });

    // Use requestAnimationFrame to prevent browser freezing on rapid calls
    requestAnimationFrame(() => {
      setNodes(updatedNodes);
    });
  },
  redistributeAllChildNodesRadial: (startNodeId: string) => {
    const {
      nodes,
      setNodes,
      childNodeDistance,
      nodeMap,
      childrenMap: storeChildrenMap,
    } = get();

    // Find the starting node
    const startNode = nodeMap.get(startNodeId);
    if (!startNode) return;

    // Convert childrenMap from Map<string, string[]> to Map<string, Node[]>
    const childrenMap = new Map<string, Node[]>();
    storeChildrenMap.forEach((childIds, parentId) => {
      const childNodes = childIds
        .map((id) => nodeMap.get(id))
        .filter(Boolean) as Node[];
      if (childNodes.length > 0) {
        childrenMap.set(parentId, childNodes);
      }
    });

    // Calculate depth of each node for distance scaling
    const nodeDepths = new Map<string, number>();
    const calculateDepth = (nodeId: string, depth: number) => {
      nodeDepths.set(nodeId, depth);
      const children = childrenMap.get(nodeId) || [];
      children.forEach((child) => calculateDepth(child.id, depth + 1));
    };
    calculateDepth(startNodeId, 0);

    const updatedPositions = new Map<string, { x: number; y: number }>();

    // Hierarchical radial layout with sector allocation (spreads outward from center)
    const layoutChildren = (
      parentId: string,
      parentAngle: number,
      angleSpan: number,
      depth: number,
    ) => {
      const children = childrenMap.get(parentId);
      if (!children || children.length === 0) return;

      const parentNode = nodes.find((n) => n.id === parentId);
      if (!parentNode) return;

      // Get parent dimensions - prefer measured over initial
      const parentWidth =
        (parentNode as any).measured?.width || (parentNode as any).width || 100;
      const parentHeight =
        (parentNode as any).measured?.height ||
        (parentNode as any).height ||
        40;

      // Distance scaling - reduced for more compact layout
      const baseDistance = childNodeDistance;
      const distance = baseDistance * Math.pow(1.4, depth) * 0.5; // More compact
      const angleStep = angleSpan / children.length;

      children.forEach((child, index) => {
        // Distribute children within their allocated angle sector
        const startAngle = parentAngle - angleSpan / 2;
        const childAngle = startAngle + (index + 0.5) * angleStep;
        const radians = (childAngle * Math.PI) / 180;

        // Position relative to parent's top-left, centered around parent
        const absolutePos = {
          x: Math.cos(radians) * distance + parentWidth / 2,
          y: Math.sin(radians) * distance + parentHeight / 2,
        };

        updatedPositions.set(child.id, absolutePos);

        // Recursively layout this child's children in their subsector
        layoutChildren(child.id, childAngle, angleStep, depth + 1);
      });
    };

    // For root level, allocate full 360° divided among direct children
    const rootChildren = childrenMap.get(startNodeId);
    if (rootChildren && rootChildren.length > 0) {
      const rootAngleStep = 360 / rootChildren.length;

      // Get parent dimensions - prefer measured over initial
      const parentNode = nodes.find((n) => n.id === startNodeId);
      const parentWidth =
        (parentNode as any)?.measured?.width ||
        (parentNode as any)?.width ||
        100;
      const parentHeight =
        (parentNode as any)?.measured?.height ||
        (parentNode as any)?.height ||
        40;

      rootChildren.forEach((child, index) => {
        const childAngle = 270 + index * rootAngleStep; // Start at top (270°)
        const distance = childNodeDistance * 0.5; // More compact

        const radians = (childAngle * Math.PI) / 180;

        // Position relative to parent's top-left, centered around parent
        const absolutePos = {
          x: Math.cos(radians) * distance + parentWidth / 2,
          y: Math.sin(radians) * distance + parentHeight / 2,
        };

        updatedPositions.set(child.id, absolutePos);

        // Layout grandchildren within this child's sector
        layoutChildren(child.id, childAngle, rootAngleStep, 1);
      });
    }

    // Apply all position updates
    const updatedNodes = nodes.map((node) => {
      const newPos = updatedPositions.get(node.id);
      if (newPos) {
        return {
          ...node,
          position: newPos,
        };
      }
      return node;
    });

    // Use requestAnimationFrame to prevent browser freezing on rapid calls
    requestAnimationFrame(() => {
      setNodes(updatedNodes);
    });
  },
  user: null,
  setUser: (user: User | null) => set({ user }),
  defaultNodeType: "nodeItem",
  setDefaultNodeType: (type: "nodeItem" | "noteItem") =>
    set({ defaultNodeType: type }),
  toastMessage: null,
  showToast: (severity: string, summary: string, detail: string) =>
    set({ toastMessage: { severity, summary, detail } }),
  clearToast: () => set({ toastMessage: null }),
  // Drill-down tabs state
  drilldownTabs: [],
  activeDrilldownTab: null,
  pendingFocusNodeId: null,
  setPendingFocusNodeId: (nodeId: string | null) =>
    set({ pendingFocusNodeId: nodeId }),
  openDrilldownTab: (nodeId: string) => {
    const {
      nodes,
      updateNodeData,
      hideChildNodes,
      drilldownTabs,
      activeDrilldownTab,
    } = get();
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    // Check if tab already exists
    const existingTab = drilldownTabs.find((tab) => tab.nodeId === nodeId);
    if (existingTab) {
      set({ activeDrilldownTab: existingTab.id });
      return;
    }

    // Create new tab
    const tabId = nanoid();
    const newTab = {
      id: tabId,
      nodeId,
      label: node.data.label || "Untitled",
      parentTabId: activeDrilldownTab,
    };

    // Mark node as having drill-down tab (preserve existing show/hide state of children)
    updateNodeData(nodeId, { isDrilldownTab: true });

    set({
      drilldownTabs: [...drilldownTabs, newTab],
      activeDrilldownTab: tabId,
    });
  },
  closeDrilldownTab: (tabId: string) => {
    const { drilldownTabs, activeDrilldownTab, updateNodeData } = get();
    const tab = drilldownTabs.find((t) => t.id === tabId);
    if (!tab) return;

    // Remove isDrilldownTab flag from node
    updateNodeData(tab.nodeId, { isDrilldownTab: false });

    // Remove tab
    const newTabs = drilldownTabs
      .filter((t) => t.id !== tabId)
      .map((t) => (t.parentTabId === tabId ? { ...t, parentTabId: null } : t));

    // If closing active tab, switch to main view or last tab
    let newActiveTab = activeDrilldownTab;
    if (activeDrilldownTab === tabId) {
      newActiveTab = newTabs.length > 0 ? newTabs[newTabs.length - 1].id : null;
    }

    set({
      drilldownTabs: newTabs,
      activeDrilldownTab: newActiveTab,
    });
  },
  setActiveDrilldownTab: (tabId: string | null) =>
    set({ activeDrilldownTab: tabId }),
  reorderDrilldownTabs: (fromIndex: number, toIndex: number) => {
    const { drilldownTabs } = get();
    const newTabs = [...drilldownTabs];
    const [removed] = newTabs.splice(fromIndex, 1);
    newTabs.splice(toIndex, 0, removed);
    set({ drilldownTabs: newTabs });
  },
}));

export { useStore, shallow };
